import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocationMapPicker } from "@/features/seller-portal/LocationMapPicker";
import { SettingsSectionCard } from "@/features/seller-portal/SettingsSectionCard";
import { SELLER_PORTAL_NS } from "@/features/seller-portal/i18n";
import { useUpdateSellerProfile } from "@/features/seller-portal/hooks";
import type { MyShopProfile } from "@/features/seller-portal/api";

export function LocationSection({
  profile,
  readOnly,
}: {
  profile: MyShopProfile;
  readOnly: boolean;
}): ReactElement {
  const { t } = useTranslation(SELLER_PORTAL_NS);
  const update = useUpdateSellerProfile();
  const [lat, setLat] = useState(profile.seller.lat ?? "");
  const [lng, setLng] = useState(profile.seller.lng ?? "");

  const onSave = (): void => {
    update.mutate(
      { lat: lat || undefined, lng: lng || undefined },
      {
        onSuccess: () => toast.success(t("settings.saved")),
        onError: () => toast.error(t("settings.saveError")),
      },
    );
  };

  return (
    <SettingsSectionCard
      title={t("settings.sections.location")}
      testid="settings-section-location"
      onSave={onSave}
      saving={update.isPending}
      disabled={readOnly}
    >
      <p className="text-xs text-[var(--g-ink-3)]">{t("settings.location.hint")}</p>
      {!readOnly ? (
        <LocationMapPicker
          lat={lat}
          lng={lng}
          onPick={(pickedLat, pickedLng) => {
            setLat(pickedLat);
            setLng(pickedLng);
          }}
        />
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="set-lat">{t("settings.location.lat")}</Label>
          <Input
            id="set-lat"
            data-testid="set-lat"
            inputMode="decimal"
            value={lat}
            disabled={readOnly}
            onChange={(e) => setLat(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="set-lng">{t("settings.location.lng")}</Label>
          <Input
            id="set-lng"
            data-testid="set-lng"
            inputMode="decimal"
            value={lng}
            disabled={readOnly}
            onChange={(e) => setLng(e.target.value)}
          />
        </div>
      </div>
    </SettingsSectionCard>
  );
}
